import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * `React.lazy` that survives a deploy.
 *
 * Every code-split view is fetched by its content hash. When a deploy replaces
 * the bundle while someone is parked on an old page — the login screen,
 * typically — the hash they ask for is gone, the dynamic import rejects, and
 * the crash screen tells a brand-new user to reload before they have seen the
 * portal even once. Signing up worked; ENTERING looked broken.
 *
 * So a failed chunk load reloads the page itself, once: the fresh HTML brings
 * the fresh hashes and the person walks straight in. The one-shot guard (per
 * session, with a cooldown) means a genuinely broken deploy still ends at the
 * crash screen instead of a reload loop.
 */
const RELOADED_AT = 'lexidy-chunk-reload-at';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyFresh<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await load();
    } catch (e) {
      let last = 0;
      try { last = Number(sessionStorage.getItem(RELOADED_AT) || 0); } catch { /* storage may be off */ }
      if (Date.now() - last > 60_000) {
        try { sessionStorage.setItem(RELOADED_AT, String(Date.now())); } catch { /* same */ }
        window.location.reload();
        // The page is on its way out — hand React a promise that never
        // settles so nothing tries to render the missing chunk meanwhile.
        return new Promise<{ default: T }>(() => {});
      }
      throw e;
    }
  });
}
